import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { sendMessage } from '../../../../../services/dmMessages';
import { useToast } from '../../../../../providers/ToastProvider';
import {
    canSendFor,
    markPendingFailed,
    pendingForPatient,
    pendingIdFor,
    removePending,
    setPendingProgress,
    toPendingMessage,
    upsertPending,
    type OutgoingMessage,
    type PendingMessage
} from './pendingMessages';
import { sendFailureNotice, sendFailureTags } from './sendFailureNotice';
import { toSendAttachment } from './stagedAttachment';

const SEND_ERROR_FALLBACK = 'The message could not be sent.';
const UNMOUNTED_FAILURE_EVENT = 'DM message send failed after its thread closed';

interface UseDmMessageSendOptions {
    patientId: string;
    patientName: string | null;
    senderName: string;
    refetchThread: () => Promise<unknown>;
    onSent?: () => void;
}

export interface DmMessageSend {
    pendingMessages: PendingMessage[];
    send: (outgoing: OutgoingMessage) => void;
    retry: (message: PendingMessage) => void;
    isSending: boolean;
}

interface SendContext {
    patientName: string | null;
}

// Set in the effect body too, so StrictMode's mount-unmount-mount leaves it true.
function useMountedRef() {
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    return mountedRef;
}

export function useDmMessageSend({
    patientId,
    patientName,
    senderName,
    refetchThread,
    onSent
}: UseDmMessageSendOptions): DmMessageSend {
    const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([]);
    const mountedRef = useMountedRef();
    const { showToast } = useToast();

    const mutation = useMutation({
        mutationFn: (outgoing: OutgoingMessage) => {
            const id = pendingIdFor(outgoing.clientRequestId);
            const onProgress = (percent: number) =>
                setPendingMessages((prev) => setPendingProgress(prev, id, percent));
            return sendMessage(
                outgoing.patientId,
                {
                    text: outgoing.text,
                    attachment: toSendAttachment(outgoing.attachment),
                    clientRequestId: outgoing.clientRequestId
                },
                onProgress
            );
        },
        onMutate: (outgoing): SendContext => {
            const pending = toPendingMessage(outgoing, senderName, new Date());
            setPendingMessages((prev) => upsertPending(prev, pending));
            return { patientName };
        },
        onSuccess: async (_message, outgoing) => {
            onSent?.();
            // Refetch first so the sent message replaces the bubble without a gap.
            await refetchThread();
            const id = pendingIdFor(outgoing.clientRequestId);
            setPendingMessages((prev) => removePending(prev, id));
        },
        onError: (err, outgoing, context) => {
            const notice = sendFailureNotice(mountedRef.current, context?.patientName ?? null);
            if (notice.kind === 'toast') {
                showToast(notice.message, 'error');
                Sentry.captureMessage(UNMOUNTED_FAILURE_EVENT, {
                    level: 'error',
                    tags: sendFailureTags(err)
                });
                return;
            }
            const message = err instanceof Error && err.message ? err.message : SEND_ERROR_FALLBACK;
            const id = pendingIdFor(outgoing.clientRequestId);
            setPendingMessages((prev) => markPendingFailed(prev, id, message));
        }
    });

    const { mutate, isPending } = mutation;

    const send = useCallback(
        (outgoing: OutgoingMessage) => {
            if (canSendFor(outgoing, patientId, isPending)) mutate(outgoing);
        },
        [patientId, isPending, mutate]
    );

    const retry = useCallback((message: PendingMessage) => send(message.outgoing), [send]);

    const visiblePending = useMemo(
        () => pendingForPatient(pendingMessages, patientId),
        [pendingMessages, patientId]
    );

    return { pendingMessages: visiblePending, send, retry, isSending: isPending };
}
